/** Sprzedaż OFFLINE: numer z puli urządzenia (ostateczny — jest na wydruku),
 * kod 2D podpisany kluczem urządzenia, transakcja w zaszyfrowanym magazynie
 * z ogniwem łańcucha hashy. Synchronizacja (sync.ts) dosyła ogniwa do SaleLight
 * — transakcja już SIĘ ODBYŁA, sync ją tylko rejestruje. */

import { kv, txns, StoredTxn } from "./db";
import { Bundle, OfferResult, Passenger } from "./engine";
import {
  bundleFreshness, consumeAllocations, DevicePool, LimitAllocation, LimitDecision,
  limitDecision, poolCheck,
} from "./policy";
import { canonical, encryptJson, sha256hex, signTicketToken } from "./vault";
import { config, isOffline } from "./api";

/** Pula numerów urządzenia — definicja jest w `policy.ts` razem z regułą jej
 * ważności; tu alias dla wołających, żeby nie zmieniać im importów. */
export type Pool = DevicePool;

export interface SellRequest {
  offer: OfferResult;
  fromUic: string; toUic: string;
  trainId?: string | null; travelAt?: string | null;
  passengers: Passenger[];
  sposOpl: number; // 0 gotówka, 1 karta (terminal zewnętrzny — decyzja JKO)
}

export interface IssuedTicket {
  seria: string; nr_bil: number; token: string; price_gr: number;
  offer_code: string; offer_name: string;
}

export async function poolState(): Promise<Pool | undefined> {
  return kv.get<Pool>("pool");
}

export async function poolRemaining(): Promise<number> {
  return poolCheck(await poolState(), new Date()).remaining;
}

/** Czy z puli urządzenia wolno jeszcze wydać numer (termin, unieważnienie, zapas). */
export async function poolStatus(now = new Date()) {
  return poolCheck(await poolState(), now);
}

async function takeNumber(now: Date): Promise<{ seria: string; nr: number }> {
  const p = await poolState();
  // Pula po terminie albo unieważniona nie jest „prawie ważna": numery z niej
  // nie wejdą do rozliczeń, więc bilet wydany teraz byłby biletem, którego nikt
  // nie przyjmie. Lepiej odmówić przy kasie niż po dniu pracy przy synchronizacji.
  const c = poolCheck(p, now);
  if (!c.ok) throw new Error(c.reason!);
  await kv.set("pool", { ...p!, next: p!.next + 1 });
  return { seria: p!.series, nr: p!.next };
}

export async function chainHead(): Promise<string> {
  return (await kv.get<string>("chain_head")) ?? "";
}

export async function sellOffline(req: SellRequest): Promise<{
  localUuid: string; tickets: IssuedTicket[]; saleAt: string;
}> {
  const cfg = await config();
  if (!cfg) throw new Error("urządzenie nieskonfigurowane");
  const bundle = await kv.get<Bundle>("bundle");
  const localUuid = crypto.randomUUID();
  const now = new Date();
  const saleAt = now.toISOString();
  const issued: IssuedTicket[] = [];
  const groups: Record<string, unknown>[] = [];

  // Te same dwie bramki, co przy wycenie — wycena mogła powstać, zanim pakiet
  // przekroczył próg albo zanim przydział limitu wygasł, a bilet wydaje się
  // TERAZ. Bramka wyłącznie przy wycenie dałaby się ominąć zwlekaniem z klikiem.
  //
  // Obie dotyczą WYŁĄCZNIE sprzedaży bez sieci. Sprzedaż urządzenia jest lokalna
  // także w trybie online, ale wtedy cena i dopuszczalność przyszły z SaleLighta
  // (świeża taryfa, limity policzone na wspólnym liczniku) — zatrzymanie jej
  // wiekiem pakietu albo brakiem przydziału byłoby odmową bez powodu.
  const bezSieci = isOffline();
  if (bezSieci && bundle) {
    const fresh = bundleFreshness(bundle.compiled_at, bundle.offline_policy, now);
    if (fresh.state === "blocked") throw new Error(fresh.reason!);
  }
  const allocations = await kv.get<LimitAllocation[]>("limit_allocations");
  const limity: LimitDecision = bezSieci
    ? limitDecision(bundle?.sale_limits, allocations, {
      offerCode: req.offer.code, units: req.offer.tickets.length,
      trainNumber: req.trainId ?? null,
      travelDay: (req.travelAt || "").slice(0, 10) || null,
      fromUic: req.fromUic, toUic: req.toUic, now,
    })
    : { reason: null, holds: [] };
  if (limity.reason) throw new Error(limity.reason);
  // Przydział pomniejszamy PRZED wydaniem biletu (jak online konsumuje pule przed
  // materializacją): przerwana sprzedaż ma oddać miejsce, a nie sprzedać je dwa razy.
  if (limity.holds.length)
    await kv.set("limit_allocations", consumeAllocations(allocations, limity.holds));
  const oddajPrzydzial = async () => {
    if (limity.holds.length) await kv.set("limit_allocations", allocations ?? []);
  };

  try {
    for (const tg of req.offer.tickets) {
      const { seria, nr } = await takeNumber(now);
      const token = await signTicketToken({
        booking: localUuid, seria, nr, amount_gr: tg.total_gr,
        offer: req.offer.code, from: req.fromUic, to: req.toUic,
        train: req.trainId ?? null, valid_until: req.offer.validityUntil,
        passengers: tg.passengers,
      }, cfg.deviceId);
      issued.push({ seria, nr_bil: nr, token, price_gr: tg.total_gr,
        offer_code: req.offer.code, offer_name: req.offer.name });
      groups.push({ ...tg, nr_bil: nr, token });
    }

    // transakcja w kształcie, który przyjmuje /osdm/offline-sync (bez ogniw łańcucha
    // — te liczymy na kanonicznej treści CAŁEJ transakcji)
    const txn: Record<string, unknown> = {
      local_uuid: localUuid,
      sale_at: saleAt,
      uzyt: cfg.conductor?.uzyt ?? null,
      spos_opl: req.sposOpl,
      price_gr: req.offer.totalGr,
      payload: {
        offer_code: req.offer.code, offer_name: req.offer.name,
        oferta: req.offer.localOfferId, ticket_type: req.offer.ticketType,
        price_gr: req.offer.totalGr, lines: req.offer.lines, tickets: groups,
        from_uic: req.fromUic, to_uic: req.toUic,
        from_name: bundle?.stations[req.fromUic]?.name ?? req.fromUic,
        to_name: bundle?.stations[req.toUic]?.name ?? req.toUic,
        train_id: req.trainId ?? null, travel_at: req.travelAt ?? null,
        validity_hours: req.offer.validityHours,
        validity_until: req.offer.validityUntil,
        passengers: req.passengers,
        // honorowanie: odcinki obcych przewoźników objęte tym biletem. Klucz i kształt
        // MUSZĄ być te same, co w propozycji online — po synchronizacji ten payload
        // idzie tą samą ścieżką materializacji (`_issue_tickets`), więc z niego
        // powstaje adnotacja na wydruku
        honoured_legs: req.offer.honouredLegs.length
          ? req.offer.honouredLegs.map((h) => ({
            from_uic: h.fromUic, to_uic: h.toUic,
            from_name: h.fromName, to_name: h.toName,
            carrier_rics: h.carrierRics, carrier_name: h.carrierName,
            train_number: h.trainNumber, note: h.note,
          }))
          : null,
        carrier: bundle?.project.carrier ?? null,
        offline_device_id: cfg.deviceId,
        // zajęcia przydzielonej urządzeniu części limitów — serwer odtwarza je
        // z payloadu przy synchronizacji, zamiast dopasowywać reguły od nowa
        // (ta sama zasada, co `local_limit_holds` przy sprzedaży online: limit
        // zmieniony po sprzedaży zostawiłby zajęcia, których nikt nie umie zwolnić)
        device_limit_holds: limity.holds.length ? limity.holds : null,
      },
    };
    const prev = await chainHead();
    txn.chain_prev = prev;
    const core = Object.fromEntries(
      Object.entries(txn).filter(([k]) => k !== "chain_prev" && k !== "chain_hash"));
    txn.chain_hash = await sha256hex(prev + canonical(core));

    const stored: StoredTxn = {
      local_uuid: localUuid, synced: false,
      envelope: await encryptJson(txn),
      chain_hash: txn.chain_hash as string,
      created_at: saleAt,
    };
    await txns.put(stored);
    await kv.set("chain_head", txn.chain_hash);
    return { localUuid, tickets: issued, saleAt };
  } catch (e) {
    await oddajPrzydzial();
    throw e;
  }
}
